import React, { useState, useMemo } from 'react';
import { OrderProcessingCard } from './OrderProcessingCard';
import { CustomModal } from '../../components/ui/CustomModal';
import { formatCurrency, formatDateForDisplay } from '../../utils/helpers';
import { ChevronsDown, ChevronsUp } from 'lucide-react';

export const OrderProcessing = ({ orders, onSaveOrder, allOrders }) => {
    const [viewingCustomer, setViewingCustomer] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [sourceFilter, setSourceFilter] = useState('All');

    // *** FIX: Removed "Hủy" from the statuses to be displayed ***
    const statusesToShow = ["Mới", "Đang tìm hàng", "Đã đặt hàng", "Đã giao hàng"];
    const statusGroupOrder = ["Mới", "Đã đặt hàng", "Đã giao hàng", "Đang tìm hàng"]; // Display order
    const sourceOptions = ['All', 'Trực tiếp', 'Website', 'Oreka', 'Lazada'];
    
    const groupedProcessingOrders = useMemo(() => {
        const filtered = orders.filter(order => 
            statusesToShow.includes(order.status) &&
            (sourceFilter === 'All' || order.source === sourceFilter)
        );

        const grouped = filtered.reduce((acc, order) => {
            const status = order.status;
            if (!acc[status]) {
                acc[status] = [];
            }
            acc[status].push(order);
            return acc;
        }, {});
        
        return grouped;
    }, [orders, sourceFilter]);

    const customerOrders = useMemo(() => {
        if (!viewingCustomer) return [];
        return allOrders
            .filter(order => order.phone === viewingCustomer.phone)
            .sort((a,b) => new Date(b.date) - new Date(a.date));
    }, [viewingCustomer, allOrders]);

    const handleViewCustomerHistory = (customer) => {
        setViewingCustomer(customer);
    };

    const handleUpdateOrder = (orderData) => {
        onSaveOrder(orderData, true);
    };

    const toggleCardExpansion = (orderId) => {
        setExpandedCards(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    const toggleAllCards = (expand) => {
        const newState = {};
        Object.values(groupedProcessingOrders).flat().forEach(order => {
            newState[order.id] = expand;
        });
        setExpandedCards(newState);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-bold text-gray-800">Xử lý đơn hàng</h2>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow-sm">
                        <label htmlFor="source-filter" className="text-sm font-medium text-gray-700">Nguồn đơn:</label>
                        <select id="source-filter" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="p-2 border rounded-md bg-white">
                            {sourceOptions.map(source => <option key={source} value={source}>{source}</option>)}
                        </select>
                    </div>
                    <button onClick={() => toggleAllCards(true)} className="flex items-center gap-2 text-sm font-semibold bg-gray-200 py-2 px-4 rounded-lg hover:bg-gray-300">
                        <ChevronsDown size={16}/> Mở tất cả
                    </button>
                    <button onClick={() => toggleAllCards(false)} className="flex items-center gap-2 text-sm font-semibold bg-gray-200 py-2 px-4 rounded-lg hover:bg-gray-300">
                        <ChevronsUp size={16}/> Đóng tất cả
                    </button>
                </div>
            </div>
            
            <CustomModal isOpen={!!viewingCustomer} onClose={() => setViewingCustomer(null)} title={`Lịch sử mua hàng: ${viewingCustomer?.name}`} maxWidth="max-w-6xl">
                 <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="border-b bg-gray-50">
                            <tr>
                                {['Mã Đơn', 'Ngày Đặt', 'Sản phẩm đã mua', 'Tổng Tiền', 'Trạng Thái'].map(h => <th key={h} className="p-3 font-semibold text-gray-600">{h}</th>)}
                            </tr>
                        </thead>
                         <tbody>
                            {customerOrders.map(order => (
                                <tr key={order.id} className="border-b">
                                    <td className="p-3 font-medium text-blue-600 align-top">{order.id}</td>
                                    <td className="p-3 align-top">{formatDateForDisplay(order.date)}</td>
                                    <td className="p-3 align-top">
                                        <ul className="list-disc list-inside text-sm">
                                            {order.items.map((item, index) => (
                                                <li key={index}>{item.productName} (x{item.quantity})</li>
                                            ))}
                                        </ul>
                                    </td>
                                    <td className="p-3 align-top">{formatCurrency(order.items.reduce((s,i) => s + i.price * i.quantity, 0))}</td>
                                    <td className="p-3 align-top">{order.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 </div>
            </CustomModal>
            
            <div className="space-y-8">
                {statusGroupOrder.map(status => {
                    const ordersInGroup = groupedProcessingOrders[status];
                    if (!ordersInGroup || ordersInGroup.length === 0) return null;

                    return (
                        <div key={status}>
                            <h3 className="text-xl font-bold text-gray-600 mb-4 pb-2 border-b-2">{status} ({ordersInGroup.length})</h3>
                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                {ordersInGroup.map(order => (
                                    <OrderProcessingCard 
                                        key={order.id} 
                                        order={order} 
                                        isExpanded={!!expandedCards[order.id]}
                                        onToggleExpansion={() => toggleCardExpansion(order.id)}
                                        onUpdate={handleUpdateOrder}
                                        onViewCustomerHistory={() => handleViewCustomerHistory({ name: order.customerName, phone: order.phone })}
                                    />
                                ))}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    );
};